// Angular
import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Libraries
import { Select, Store } from '@ngxs/store';
import { Observable, filter, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LoadingBarService } from '@ngx-loading-bar/core';

// Services
import { DynamicSideNavService } from '../dynamic-side-nav/services/dynamic-side-nav.service';
import { NotificationService, UserProfileService } from '../../services';
import { JobService } from '../../../modules/job/services/job.service';
import { HeaderService } from './services/header.service';
import { UserService } from '../../../modules/user/services/user.service';

// Components
import { UserEditSimpleDialogComponent } from '../../../dialogs/user-edit-dialog/user-edit-dialog.component';
import { UserChannelSettingDialogComponent } from '../../../dialogs/user-channel-settings-dialog/user-channel-settings-dialog.component';
import { ThemeSettingsDialogComponent } from '../../../dialogs/theme-settings-dialog/theme-settings-dialog.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';

// Models
import { AppCleanup, NotificationModel, UrlToOnboardingKeyMapping, UserModel } from '../../models';
import { HorizontalItem } from '../../models/horizontal-item.model';

// Helpers
import { UtilityHelper } from '../../helpers';

// Store
import { AuthActions } from '../../../modules/auth/state/auth.action';
import { UserAuthState } from '../../../modules/auth/state/user-auth.state';
import { UserAuthActions } from '../../../modules/auth/state/user-auth.action';
import { StorageState, SideNavModel } from '../../state/storage.state';
import { StorageActions } from '../../state/storage.action';
import { DashboardActions } from '../../../modules/job/components/dashboard/state/dashboard.action';

// Enums
import { StatusType } from '../../enums';
import { OnboardingKey } from '../../types/types';

@Component({
	selector: 'header',
	templateUrl: './header.component.html',
	styleUrl: './header.component.scss',
})
export class HeaderComponent extends AppCleanup implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(UserAuthState.currentUser) currentUser$: Observable<UserModel>;
	@Select(StorageState.sideNav) sideNav$: Observable<SideNavModel>;
	sideNav: SideNavModel;
	sideNavOpened: boolean = false;
	dynamicSideNavOpened: boolean = false;
	currentUser: UserModel;
	currentUserPrimaryRole: string;
	isFullScreen: boolean = false;
	onboardingKey: OnboardingKey = undefined;
	currentRouteUrl: string = '';
	menuList: HorizontalItem[];
	notificationMenuOpen: boolean = false;
	profilePicture: string = '../../../assets/images/profile-picture.png';
	private readonly urlToOnboardingKeyMappings: UrlToOnboardingKeyMapping[] = [
		{
			exactMatch: '/job',
			onboardingKey: 'active-jobs',
		},
		{
			startsWith: '/job',
			contains: '/dashboard',
			onboardingKey: 'dashboard',
		},
		{
			startsWith: '/job',
			contains: '/force-monitor',
			onboardingKey: 'force-monitor',
		},
		{
			startsWith: '/job',
			contains: '/daily-reports',
			onboardingKey: 'daily-reports',
		},
		{
			startsWith: '/job',
			contains: '/raw-data',
			onboardingKey: 'raw-data',
		},
		{
			startsWith: '/job',
			regExp: new RegExp(/\/job\/[0-9]+$/),
			onboardingKey: 'job-overview',
		},
		{
			exactMatch: '/job/complete',
			onboardingKey: 'completed-jobs',
		},
	];

	@ViewChild('notificationsMenuTrigger') notificationsMenuTrigger;

	constructor(
		private notificationService: NotificationService,
		private dialog: MatDialog,
		private translateService: TranslateService,
		private jobService: JobService,
		private router: Router,
		private store: Store,
		private userProfileService: UserProfileService,
		private headerService: HeaderService,
		private dynamicSideNavService: DynamicSideNavService,
		public loader: LoadingBarService,
		private userService: UserService
	) {
		super();
		this.processTimePassed();

		setInterval(() => {
			this.processTimePassed();
		}, 15000);

		this.router.events.pipe(takeUntil(this.destroy)).subscribe((value) => {
			if (value instanceof NavigationEnd) {
				this.currentRouteUrl = value.urlAfterRedirects;
				this.onboardingKey = undefined;
				this.updateOnboardingKey();
			}
		});
	}

	ngOnInit() {
		this.currentUser$.pipe(takeUntil(this.destroy)).subscribe((currentUser) => {
			if (currentUser) {
				this.currentUser = UtilityHelper.deepCopy(currentUser);
				this.currentUserPrimaryRole = this.userService.getUserPrimaryRole(currentUser.roles);
			}
		});

		this.headerService.menuList$.pipe(takeUntil(this.destroy)).subscribe((menuList) => {
			if (menuList) {
				this.menuList = menuList;
			}
		});

		this.sideNav$.pipe(takeUntil(this.destroy)).subscribe((sideNav) => {
			this.sideNav = UtilityHelper.deepCopy(sideNav);
			if (this.sideNav) {
				this.sideNavOpened = sideNav.opened;
			} else {
				this.sideNav = new SideNavModel();
			}
		});

		this.dynamicSideNavService.opened.pipe(takeUntil(this.destroy)).subscribe((opened) => {
			this.dynamicSideNavOpened = opened;
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	openDropdown() {
		this.onboardingKey = undefined;
		this.updateOnboardingKey();
	}

	isActive(item) {
		if (item.page === this.currentRouteUrl) {
			return true;
		}

		return false;
	}

	toggleNav() {
		this.sideNav.opened = !this.sideNav.opened;
		this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
	}

	toggleMobileSideNav() {
		this.dynamicSideNavOpened = !this.dynamicSideNavOpened;
		this.dynamicSideNavService.opened.next(this.dynamicSideNavOpened);
	}

	isIos() {
		return UtilityHelper.isIos(); // Checking if iOS
	}

	toggleFullScreen() {
		if (this.isFullScreen) {
			this.closeFullScreen();
		} else {
			this.openFullScreen();
		}
	}

	openFullScreen() {
		// Trigger fullscreen
		const docElmWithBrowsersFullScreenFunctions = document.body as HTMLElement & {
			mozRequestFullScreen(): Promise<void>;
			webkitRequestFullscreen(): Promise<void>;
			msRequestFullscreen(): Promise<void>;
		};

		if (docElmWithBrowsersFullScreenFunctions.requestFullscreen) {
			docElmWithBrowsersFullScreenFunctions.requestFullscreen();
		} else if (docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen) {
			/* Firefox */
			docElmWithBrowsersFullScreenFunctions.mozRequestFullScreen();
		} else if (docElmWithBrowsersFullScreenFunctions.webkitRequestFullscreen) {
			/* Chrome, Safari and Opera */
			docElmWithBrowsersFullScreenFunctions.webkitRequestFullscreen();
		} else if (docElmWithBrowsersFullScreenFunctions.msRequestFullscreen) {
			/* IE/Edge */
			docElmWithBrowsersFullScreenFunctions.msRequestFullscreen();
		}
		this.isFullScreen = true;
	}

	closeFullScreen() {
		const docWithBrowsersExitFunctions = document as Document & {
			mozCancelFullScreen(): Promise<void>;
			webkitExitFullscreen(): Promise<void>;
			msExitFullscreen(): Promise<void>;
		};
		if (docWithBrowsersExitFunctions.exitFullscreen) {
			docWithBrowsersExitFunctions.exitFullscreen();
		} else if (docWithBrowsersExitFunctions.mozCancelFullScreen) {
			/* Firefox */
			docWithBrowsersExitFunctions.mozCancelFullScreen();
		} else if (docWithBrowsersExitFunctions.webkitExitFullscreen) {
			/* Chrome, Safari and Opera */
			docWithBrowsersExitFunctions.webkitExitFullscreen();
		} else if (docWithBrowsersExitFunctions.msExitFullscreen) {
			/* IE/Edge */
			docWithBrowsersExitFunctions.msExitFullscreen();
		}
		this.isFullScreen = false;
	}

	get notificationCount(): number | undefined {
		return this.notificationService?.notifications?.getValue().length;
	}

	get getNotifications(): NotificationModel[] | [] {
		let notifications = this.notificationService?.notifications?.getValue();
		return notifications;
	}

	processTimePassed() {
		this.notificationService.notifications.pipe(takeUntil(this.destroy)).subscribe((notifications) => {
			notifications.forEach((notification: NotificationModel) => {
				notification.timePassed = this.getTimeDiff(notification);
			});
		});
	}

	getTimeDiff(notification: NotificationModel): string {
		let lastDate = Date.now();
		let messageDate = notification.messageDate.getTime();

		if (!lastDate) {
			return '--';
		}

		let totalSeconds = Math.floor((lastDate - messageDate) / 1000); // seconds total
		let totalDays = Math.floor(totalSeconds / (60 * 60 * 24));
		totalSeconds %= 60 * 60 * 24;
		let totalHours = Math.floor(totalSeconds / (60 * 60));
		totalSeconds %= 60 * 60;
		let totalMinutes = Math.floor(totalSeconds / 60);

		let totalTime: string = '';
		if (totalDays > 0) totalTime += `${totalDays}d `;
		if (totalHours > 0) totalTime += `${totalHours}h `;
		if (totalMinutes > 0) totalTime += `${totalMinutes}m `;

		return totalTime || 'Less than a minute';
	}

	deleteNotification(index) {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.NOTIFICATION.TITLE') })}`,
			},
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: boolean) => {
				if (data) {
					if (this.notificationMenuOpen && this.getNotifications.length === 1) {
						this.notificationsMenuTrigger.closeMenu();
					}

					setTimeout(() => {
						// Adding timeout, because once notification are cleared and menu is open, it errors out
						this.notificationService.deleteNotification(index);
					}, 300);
				}
			});
	}

	clearNotifications() {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				message: `${this.translateService.instant('GENERIC.CONFIRM_CLEAR_ALL', { name: this.translateService.instant('MODULE.NOTIFICATION.TITLE_PLURAL') })}`,
			},
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: boolean) => {
				if (data) {
					if (this.notificationMenuOpen) {
						this.notificationsMenuTrigger.closeMenu();
					}

					setTimeout(() => {
						// Adding timeout, because once notification are cleared and menu is open, it errors out
						this.notificationService.clearNotifications();
					}, 300);
				}
			});
	}

	takeToRoute(event, notification: NotificationModel) {
		if (!notification.route) {
			return;
		}

		if (notification.toJob?.id) {
			this.jobService.initJob(String(notification.toJob.id), false, true);
			this.store.dispatch(new DashboardActions.GetAll({ jobKey: String(notification.toJob.id), showSpinner: false, showToast: false }));
		}

		this.router.navigateByUrl(notification.route);
	}

	openUserDialog() {
		if (this.currentUser) {
			let dialogRef = this.dialog.open(UserEditSimpleDialogComponent, {
				data: {
					id: this.currentUser.id,
				},
				width: '100%',
				maxWidth: '400px',
				autoFocus: false,
			});

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe(() => {});
		}
	}

	openUserChannelSettingsDialog() {
		if (this.currentUser) {
			let dialogRef = this.dialog.open(UserChannelSettingDialogComponent, {
				data: {
					id: this.currentUser.id,
				},
				autoFocus: false,
				width: '100%',
				maxWidth: '700px',
			});

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe(() => {});
		}
	}

	openThemeSettings() {
		if (this.currentUser) {
			let dialogRef = this.dialog.open(ThemeSettingsDialogComponent, {
				autoFocus: false,
				width: '100%',
				maxWidth: '400px',
			});

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe(() => {});
		}
	}

	initializeOnboarding() {
		if (this.onboardingKey) {
			this.userProfileService.initializeOnboarding(this.onboardingKey);
		}
	}

	updateOnboardingKey() {
		const pathName = this.getPathName();

		const pathIsExactMatchWith = (mapping: UrlToOnboardingKeyMapping, path: string): boolean => {
			return mapping?.exactMatch === path;
		};
		const pathStartsWith = (mapping: UrlToOnboardingKeyMapping, path: string): boolean => {
			return mapping?.startsWith && path.startsWith(mapping.startsWith);
		};

		const pathContains = (mapping: UrlToOnboardingKeyMapping, path: string): boolean => {
			return mapping?.contains && path.includes(mapping.contains);
		};

		const pathHasRegExpMatch = (mapping: UrlToOnboardingKeyMapping, path: string): boolean => {
			return mapping?.regExp && mapping.regExp?.test(path);
		};

		this.urlToOnboardingKeyMappings.forEach((mapping: UrlToOnboardingKeyMapping) => {
			if (this.onboardingKey === undefined) {
				if (pathIsExactMatchWith(mapping, pathName)) {
					this.onboardingKey = mapping.onboardingKey;
				} else if (pathStartsWith(mapping, pathName) && (pathContains(mapping, pathName) || pathHasRegExpMatch(mapping, pathName))) {
					this.onboardingKey = mapping.onboardingKey;
				}
			}
		});
	}

	getPathName(): string {
		return window.location.pathname.toLowerCase();
	}

	logout() {
		this.store.dispatch(new UserAuthActions.DeleteUser());
		this.store.dispatch(new AuthActions.DeleteToken());
	}

	get StatusType() {
		return StatusType;
	}
}
